import type { ComponentType } from "react"

import type { GraphMeeting, HomeContextResponse } from "@/api/home-context"
import type { ChatHomeLayout, ChatHomeSignals } from "@/types/user-settings"
import type { TFunction } from "i18next"

import {
  Brain,
  BriefcaseBusiness,
  CalendarDays,
  FileText,
  Mail,
  TrendingUp,
  Users,
} from "@/components/icons"

export const DEFAULT_CHAT_HOME_LAYOUT: ChatHomeLayout = "a2-rail"
export const DEFAULT_CHAT_HOME_SIGNALS: ChatHomeSignals = {
  meetings: true,
  unreadMail: true,
  recentFiles: true,
  trending: true,
  people: true,
  projects: true,
  memoryNudges: true,
  // Day Brief sub-sections all default ON ("all-in" v1). Users can hide
  // individual sections from the Preferences general panel.
  dayBriefInbox: true,
  dayBriefProjectPulse: true,
  dayBriefWaitingOnYou: true,
  dayBriefTomorrow: true,
}

export type SnapshotSignal = {
  id: keyof ChatHomeSignals | "more"
  label: string
  value: string
  detail?: string | null
  icon: ComponentType<{ className?: string }>
  menuItems?: SnapshotSignalMenuItem[]
  actionLabel?: string
  onClick?: () => void
}

export type SnapshotSignalMenuItem = {
  id: string
  label: string
  description?: string | null
  onSelect: () => void
}

export function isChatHomeLayout(value: unknown): value is ChatHomeLayout {
  return value === "a1-strip" || value === "a2-rail"
}

function signalCount(count: number, singular: string, plural: string): string {
  return count === 1 ? `1 ${singular}` : `${count} ${plural}`
}

function firstHttpsUrl(url?: string | null): string | null {
  return typeof url === "string" && /^https:\/\//i.test(url) ? url : null
}

function isConcludedMeeting(meeting: GraphMeeting): boolean {
  const endIso = meeting.end_iso || meeting.start_iso
  if (!endIso) return false
  const end = new Date(endIso).getTime()
  return Number.isFinite(end) && end < Date.now()
}

function isOngoingMeeting(meeting: GraphMeeting): boolean {
  if (!meeting.start_iso || !meeting.end_iso) return false
  const now = Date.now()
  const start = new Date(meeting.start_iso).getTime()
  const end = new Date(meeting.end_iso).getTime()
  return Number.isFinite(start) && Number.isFinite(end) && start <= now && end >= now
}

function meetingPeople(meeting: GraphMeeting): string {
  return meeting.attendees
    .map((attendee) => attendee.name || attendee.email)
    .filter((value): value is string => Boolean(value))
    .join(", ")
}

function meetingPrompt(meeting: GraphMeeting, t: TFunction): string {
  const subject = meeting.subject || t("chatHome.snapshot.menu.untitledMeeting", "Untitled meeting")
  const people = meetingPeople(meeting)
  const attendeesLine = people
    ? t("chatHome.snapshot.prompts.meetingAttendees", {
        attendees: people,
        defaultValue: "Attendees: {{attendees}}.",
      })
    : ""
  const agendaLine = meeting.preview
    ? t("chatHome.snapshot.prompts.meetingAgendaPreview", {
        defaultValue: "Agenda preview: {{preview}}",
        preview: meeting.preview,
      })
    : ""
  let lines: string[]
  if (isOngoingMeeting(meeting)) {
    lines = [
      t("chatHome.snapshot.prompts.meetingOngoingIntro", {
        defaultValue: 'Catch me up on the ongoing meeting: "{{subject}}".',
        subject,
      }),
      attendeesLine,
      agendaLine,
      t(
        "chatHome.snapshot.prompts.meetingOngoingInstructions",
        "Summarize likely context, decisions so far, open questions, and what I should do next."
      ),
    ]
  } else if (isConcludedMeeting(meeting)) {
    lines = [
      t("chatHome.snapshot.prompts.meetingConcludedIntro", {
        defaultValue: 'Summarize and wrap up the concluded meeting: "{{subject}}".',
        subject,
      }),
      attendeesLine,
      agendaLine,
      t(
        "chatHome.snapshot.prompts.meetingConcludedInstructions",
        "Highlight decisions, open questions, and follow-up actions."
      ),
    ]
  } else {
    lines = [
      t("chatHome.snapshot.prompts.meetingUpcomingIntro", {
        defaultValue: 'Help me prep for my upcoming meeting: "{{subject}}".',
        subject,
      }),
      attendeesLine,
      agendaLine,
      t(
        "chatHome.snapshot.prompts.meetingUpcomingInstructions",
        "Summarize what's likely on the table, surface recent related context, and suggest 3 talking points."
      ),
    ]
  }
  return lines.filter(Boolean).join("\n")
}

function meetingMenuDescription(meeting: GraphMeeting, t: TFunction): string {
  if (isOngoingMeeting(meeting)) {
    return t("chatHome.snapshot.menu.ongoingMeeting", "In progress")
  }
  if (isConcludedMeeting(meeting)) {
    return t("chatHome.snapshot.menu.wrapMeeting", "Summary and wrap-up")
  }
  return t("chatHome.snapshot.menu.prepMeeting", "Prep meeting")
}

export function buildSnapshotSignals({
  personalization,
  enabledSignals,
  t,
  onExampleQuestionClick,
  onMeetingPrep,
  onTrendingClick,
  onMemoryNudgeClick,
}: {
  personalization: HomeContextResponse
  enabledSignals: ChatHomeSignals
  t: TFunction
  onExampleQuestionClick: (example: string) => void
  onMeetingPrep?: (meeting: GraphMeeting) => void
  onTrendingClick?: () => void
  onMemoryNudgeClick?: () => void
}): SnapshotSignal[] {
  const graph = personalization.graph
  const helloai = personalization.helloai
  const signals: SnapshotSignal[] = []
  const todayMeetings = graph.today_meetings?.length
    ? graph.today_meetings
    : graph.upcoming_meetings
  const meetingCount = todayMeetings.length
  const unreadMailCount = graph.unread_mail?.count ?? 0
  const recentFileCount = graph.recent_files.length
  const trendingCount = graph.trending.length
  const peopleCount = graph.people.length
  const projectCount = helloai.active_projects.length
  const memoryCount = helloai.pending_memory_candidates

  if (enabledSignals.meetings && meetingCount > 0) {
    const firstMeeting = graph.upcoming_meetings[0] ?? todayMeetings[0]
    const prepMeeting = Boolean(onMeetingPrep && firstMeeting.start_iso)
    signals.push({
      id: "meetings",
      label: t("chatHome.snapshot.meetingsLabel", "Meetings"),
      value: t("chatHome.snapshot.meetings", {
        count: meetingCount,
        defaultValue: signalCount(meetingCount, "meeting today", "meetings today"),
      }),
      detail: firstMeeting.subject,
      icon: CalendarDays,
      actionLabel: t("chatHome.snapshot.actions.chooseMeetings", {
        defaultValue: "Choose meetings to prep or wrap up",
      }),
      menuItems: [
        {
          id: "meetings:all",
          label: t("chatHome.snapshot.menu.allMeetings", {
            defaultValue: "All meetings today",
          }),
          description: t("chatHome.snapshot.menu.allMeetingsDescription", {
            defaultValue: "Summarize the day and pick priorities.",
          }),
          onSelect: () =>
            onExampleQuestionClick(
              t("chatHome.snapshot.prompts.prepareMeetings", {
                defaultValue:
                  "Summarize my meetings today. Help me choose what to prepare for, what to wrap up, and which follow-ups matter most.",
              })
            ),
        },
        ...todayMeetings.map((meeting) => ({
          id: `meetings:${meeting.id}`,
          label: meeting.subject || t("chatHome.snapshot.menu.untitledMeeting", "Untitled meeting"),
          description: meetingMenuDescription(meeting, t),
          onSelect: () => onExampleQuestionClick(meetingPrompt(meeting, t)),
        })),
      ],
      onClick: prepMeeting ? () => onMeetingPrep?.(firstMeeting) : undefined,
    })
  }

  if (enabledSignals.unreadMail && unreadMailCount > 0) {
    signals.push({
      id: "unreadMail",
      label: t("chatHome.snapshot.unreadMailLabel", "Mail"),
      value: t("chatHome.snapshot.unreadMail", {
        count: unreadMailCount,
        defaultValue: signalCount(unreadMailCount, "unread email", "unread emails"),
      }),
      detail: graph.unread_mail?.messages[0]?.subject,
      icon: Mail,
      actionLabel: t("chatHome.snapshot.actions.summarizeMail", {
        defaultValue: "Choose unread mail to triage",
      }),
      menuItems: [
        {
          id: "unreadMail:all",
          label: t("chatHome.snapshot.menu.triageInbox", "Triage inbox"),
          description: t("chatHome.snapshot.menu.triageInboxDescription", {
            defaultValue: "Urgency, themes, and next actions.",
          }),
          onSelect: () =>
            onExampleQuestionClick(
              t("chatHome.snapshot.prompts.summarizeUnreadMail", {
                defaultValue:
                  "Triage my unread inbox. Summarize urgent messages, group related threads, and suggest next actions.",
              })
            ),
        },
        ...(graph.unread_mail?.messages ?? []).map((message) => ({
          id: `unreadMail:${message.id}`,
          label: message.subject || t("chatHome.snapshot.menu.untitledEmail", "Untitled email"),
          description: message.sender,
          onSelect: () => {
            const subject =
              message.subject || t("chatHome.snapshot.menu.untitledEmail", "Untitled email")
            const translationKey = message.sender
              ? "chatHome.snapshot.prompts.summarizeUnreadEmailWithSender"
              : "chatHome.snapshot.prompts.summarizeUnreadEmail"
            onExampleQuestionClick(
              t(translationKey, {
                defaultValue: message.sender
                  ? 'Summarize this unread email from {{sender}} and tell me if it needs action: "{{subject}}".'
                  : 'Summarize this unread email and tell me if it needs action: "{{subject}}".',
                sender: message.sender,
                subject,
              })
            )
          },
        })),
      ],
    })
  }

  if (enabledSignals.recentFiles && recentFileCount > 0) {
    signals.push({
      id: "recentFiles",
      label: t("chatHome.snapshot.recentFilesLabel", "Files"),
      value: t("chatHome.snapshot.recentFiles", {
        count: recentFileCount,
        defaultValue: signalCount(recentFileCount, "recent file", "recent files"),
      }),
      detail: graph.recent_files[0]?.name,
      icon: FileText,
      actionLabel: t("chatHome.snapshot.actions.askRecentFiles", {
        defaultValue: "Choose recent files to review",
      }),
      menuItems: [
        {
          id: "recentFiles:all",
          label: t("chatHome.snapshot.menu.reviewRecentFiles", "Review recent files"),
          description: t("chatHome.snapshot.menu.reviewRecentFilesDescription", {
            defaultValue: "Summarize and suggest what to review.",
          }),
          onSelect: () =>
            onExampleQuestionClick(
              t("chatHome.snapshot.prompts.summarizeRecentFiles", {
                defaultValue: "Summarize my recent files and suggest what to review next.",
              })
            ),
        },
        ...graph.recent_files.map((file) => ({
          id: `recentFiles:${file.id}`,
          label: file.name || t("chatHome.snapshot.menu.untitledFile", "Untitled file"),
          description: firstHttpsUrl(file.weburl)
            ? t("chatHome.snapshot.menu.fileAvailable", "Available in Microsoft 365")
            : null,
          onSelect: () =>
            onExampleQuestionClick(
              t("chatHome.snapshot.prompts.summarizeRecentFile", {
                defaultValue:
                  'Summarize and review this recent file: "{{name}}". Tell me what needs attention.',
                name: file.name || t("chatHome.snapshot.menu.untitledFile", "Untitled file"),
              })
            ),
        })),
      ],
    })
  }

  if (enabledSignals.trending && trendingCount > 0) {
    signals.push({
      id: "trending",
      label: t("chatHome.trending", "Trending"),
      value: t("chatHome.snapshot.trending", {
        count: trendingCount,
        defaultValue: signalCount(trendingCount, "trending file", "trending files"),
      }),
      detail: graph.trending[0]?.name,
      icon: TrendingUp,
      actionLabel: onTrendingClick
        ? t("chatHome.snapshot.actions.openTrending", {
            defaultValue: "Choose trending files",
          })
        : t("chatHome.snapshot.actions.askTrending", {
            defaultValue: "Choose trending files",
          }),
      menuItems: [
        {
          id: "trending:all",
          label: t("chatHome.snapshot.menu.explainTrending", "Explain trending items"),
          description: t("chatHome.snapshot.menu.explainTrendingDescription", {
            defaultValue: "Why they may matter now.",
          }),
          onSelect:
            onTrendingClick ??
            (() =>
              onExampleQuestionClick(
                t("chatHome.snapshot.prompts.showTrending", {
                  defaultValue:
                    "Show me what is trending in my work context and explain why it may matter.",
                })
              )),
        },
        ...graph.trending.map((file) => ({
          id: `trending:${file.id}`,
          label: file.name || t("chatHome.snapshot.menu.untitledFile", "Untitled file"),
          description: t("chatHome.snapshot.menu.trendingFile", "Explain relevance"),
          onSelect: () =>
            onExampleQuestionClick(
              t("chatHome.snapshot.prompts.explainTrendingFile", {
                defaultValue:
                  'Explain why this file is trending and what I should know about it: "{{name}}".',
                name: file.name || t("chatHome.snapshot.menu.untitledFile", "Untitled file"),
              })
            ),
        })),
      ],
    })
  }

  if (enabledSignals.people && peopleCount > 0) {
    const firstPersonName = graph.people[0]?.name
    signals.push({
      id: "people",
      label: t("chatHome.snapshot.peopleLabel", "People"),
      value: t("chatHome.snapshot.people", {
        count: peopleCount,
        defaultValue: signalCount(peopleCount, "collaborator", "collaborators"),
      }),
      detail: firstPersonName,
      icon: Users,
      actionLabel: t("chatHome.snapshot.actions.followUpPeople", {
        defaultValue: "Choose collaborators for follow-up",
      }),
      menuItems: [
        {
          id: "people:all",
          label: t("chatHome.snapshot.menu.followUpCollaborators", "Follow up with collaborators"),
          description: t("chatHome.snapshot.menu.followUpCollaboratorsDescription", {
            defaultValue: "Draft priorities across people.",
          }),
          onSelect: () =>
            onExampleQuestionClick(
              t("chatHome.snapshot.prompts.followUpPeople", {
                defaultValue: "Help me follow up with my collaborators.",
              })
            ),
        },
        ...graph.people.map((person) => ({
          id: `people:${person.id}`,
          label:
            person.name ||
            person.email ||
            t("chatHome.snapshot.menu.unnamedPerson", "Unnamed person"),
          description: person.job_title,
          onSelect: () =>
            onExampleQuestionClick(
              t("chatHome.snapshot.prompts.followUpPerson", {
                defaultValue: "Help me follow up with {{name}}.",
                name: person.name || person.email || "this collaborator",
              })
            ),
        })),
      ],
    })
  }

  if (enabledSignals.projects && projectCount > 0) {
    const firstProjectName = helloai.active_projects[0]?.name
    signals.push({
      id: "projects",
      label: t("chatHome.snapshot.projectsLabel", "Projects"),
      value: t("chatHome.snapshot.projects", {
        count: projectCount,
        defaultValue: signalCount(projectCount, "active project", "active projects"),
      }),
      detail: firstProjectName,
      icon: BriefcaseBusiness,
      actionLabel: t("chatHome.snapshot.actions.planProject", {
        defaultValue: "Choose active projects",
      }),
      menuItems: [
        {
          id: "projects:all",
          label: t(
            "chatHome.snapshot.menu.allProjectStatus",
            "Project status across active projects"
          ),
          description: t("chatHome.snapshot.menu.allProjectStatusDescription", {
            defaultValue: "Status, blockers, and next actions.",
          }),
          onSelect: () =>
            onExampleQuestionClick(
              t("chatHome.snapshot.prompts.projectStatusAll", {
                defaultValue:
                  "Summarize the status of my active projects, including blockers and next actions.",
              })
            ),
        },
        ...helloai.active_projects.map((project) => ({
          id: `projects:${project.id}`,
          label: project.name,
          description: t("chatHome.snapshot.menu.projectStatus", "Project status"),
          onSelect: () =>
            onExampleQuestionClick(
              t("chatHome.snapshot.prompts.projectStatus", {
                defaultValue:
                  "Summarize the current status of {{name}}. Include recent activity, blockers, and likely next actions.",
                name: project.name,
              })
            ),
        })),
      ],
    })
  }

  if (enabledSignals.memoryNudges && memoryCount > 0) {
    signals.push({
      id: "memoryNudges",
      label: t("chatHome.snapshot.memoryLabel", "Memory"),
      value: t("chatHome.snapshot.memoryNudges", {
        count: memoryCount,
        defaultValue: signalCount(memoryCount, "memory update", "memory updates"),
      }),
      icon: Brain,
      actionLabel: t("chatHome.snapshot.actions.reviewMemory", {
        defaultValue: "Review memory updates",
      }),
      onClick: onMemoryNudgeClick,
    })
  }

  return signals
}
